import Phaser from "phaser";
import { InputHandler } from "../controller/InputHandler";
import {
  LevelSelectController,
  type LevelSelectDirection,
  type LevelSelectNodeState,
  type LevelSelectRoute,
  type LevelSelectState,
  type LevelSelectTransitionResult,
} from "../controller/LevelSelectController";
import { getRuntimeSession } from "../core/RuntimeSession";
import type { ActionType } from "../types/action";
import type { ChapterData } from "../types/chapter";
import type { LevelData } from "../types/level";
import { LevelSelectRenderer } from "../view/LevelSelectRenderer";
import { LevelScene } from "./LevelScene";
import { DEFAULT_BGM_VOLUME, LEVEL_SELECT_BGM_KEY } from "./BootScene";

const PASSWORD_UNLOCK_SUCCESS_FLASH_MS = 250;
const PASSWORD_FAILURE_FLASH_MS = 500;
const END_UNDO_FLASH_DURATION_MS = 100;
const LEVEL_SELECT_TO_LEVEL_FADE_IN_MS = 500;
const ENDING_LEVEL_ID = "ending-0";

interface LevelSelectTransitionInData {
  readonly color: "white" | "black";
  readonly durationMs: number;
}

export interface LevelSelectSceneData {
  readonly route?: LevelSelectRoute;
  readonly transitionIn?: LevelSelectTransitionInData;
}

export class LevelSelectScene extends Phaser.Scene {
  public static readonly KEY = "LevelSelectScene";

  private readonly inputHandler = new InputHandler();
  private controller: LevelSelectController | null = null;
  private keyboardListener: ((event: KeyboardEvent) => void) | null = null;
  private route: LevelSelectRoute = {};
  private levelSelectRenderer: LevelSelectRenderer | null = null;
  private isInputLocked: boolean = false;
  private rejectionTween: Phaser.Tweens.Tween | null = null;
  private runtimeToken: number = 0;
  private chapters: readonly ChapterData[] = [];
  private levels: Readonly<Record<string, LevelData>> = {};
  private levelSelectBgm: Phaser.Sound.BaseSound | null = null;
  private passwordInputFlash: boolean = false;
  private passwordInputFlashTimer: Phaser.Time.TimerEvent | null = null;
  private transitionIn: LevelSelectTransitionInData | null = null;
  private transitionInOverlay: Phaser.GameObjects.Rectangle | null = null;
  private transitionInTween: Phaser.Tweens.Tween | null = null;

  public constructor() {
    super(LevelSelectScene.KEY);
  }

  public init(data: LevelSelectSceneData = {}): void {
    this.route = data.route ?? {};
    this.transitionIn = data.transitionIn ?? null;
  }

  public create(): void {
    this.cameras.main.setBackgroundColor("#000000");
    this.runtimeToken += 1;

    const runtimeSession = getRuntimeSession();
    const assets = runtimeSession.getAssets();
    this.chapters = Object.values(assets.chapters).sort((a, b) => a.order - b.order);
    this.levels = assets.levels;

    this.controller = new LevelSelectController(this.chapters, runtimeSession.getCurrentSave(), this.route);
    this.levelSelectRenderer = new LevelSelectRenderer(this);
    this.playLevelSelectBgm();
    this.renderState(this.controller.getState());
    this.publishDebugState(this.controller.getState());
    this.startTransitionIn();
    this.setupInput();
    this.bindCleanup();
  }

  private setupInput(): void {
    const keyboard = this.input.keyboard;
    if (keyboard === undefined || keyboard === null || this.controller === null) {
      return;
    }

    this.keyboardListener = (event: KeyboardEvent) => {
      void this.handleKeyboardInput(event.code);
    };

    keyboard.on("keydown", this.keyboardListener);
  }

  private async handleKeyboardInput(raw: string): Promise<void> {
    if (this.isInputLocked || this.controller === null) {
      return;
    }

    const state = this.controller.getState();
    if (raw === "Escape" || raw === "Esc") {
      if (state.mode.kind === "passwordLock") {
        this.cancelPasswordInputFlash();
        const result = this.controller.cancelPasswordLock();
        this.publishDebugState(result.state);
        this.renderState(result.state);
      }
      return;
    }

    if (raw === "Enter" || raw === "NumpadEnter" || raw === "KeyE") {
      const result = state.mode.kind === "passwordLock"
        ? this.controller.submitPasswordLock()
        : this.controller.activateFocused();
      this.publishDebugState(result.state);
      if (result.signal.kind === "launchLevel") {
        this.scene.start(LevelScene.KEY, {
          levelId: result.signal.levelId,
          transitionIn: {
            color: "black",
            durationMs: LEVEL_SELECT_TO_LEVEL_FADE_IN_MS,
          },
        });
      } else if (result.signal.kind === "launchEnding") {
        this.launchEndingLevel();
      } else if (result.signal.kind === "blocked") {
        this.startRejectionAnimation(result.state, undefined, undefined);
      } else if (result.signal.kind === "passwordUnlocked") {
        await this.handlePasswordUnlock(result);
      } else if (result.signal.kind === "passwordAttemptFailed") {
        await this.handlePasswordFailure(result);
      } else {
        this.cancelPasswordInputFlash();
        this.renderState(result.state);
      }
      return;
    }

    const mapping = this.inputHandler.map(raw);
    if (state.mode.kind === "passwordLock") {
      const passwordMode = state.mode;
      if (isActionType(mapping.mapped)) {
        const chapter = this.chapters.find((c) => c.id === passwordMode.chapterId);
        const isEndChapter = chapter?.kind === "ending";

        if (isEndChapter && mapping.mapped === "undo") {
          this.triggerWhiteFlash();
        }

        const result = this.controller.recordPasswordAction(mapping.mapped);
        this.publishDebugState(result.state);

        if (result.signal.kind === "launchEnding") {
          this.launchEndingLevel();
        } else if (result.signal.kind === "passwordUnlocked") {
          this.cancelPasswordInputFlash();
          await this.handlePasswordUnlock(result);
        } else if (result.signal.kind === "passwordAttemptFailed") {
          await this.handlePasswordFailure(result);
        } else {
          this.triggerPasswordInputFlash();
        }
      }
      return;
    }

    if (isLevelSelectDirection(mapping.mapped)) {
      const direction = mapping.mapped;
      const result = this.controller.move(direction);
      this.publishDebugState(result.state);

      if (result.signal.kind === "blocked") {
        const rejectedNodeId = result.signal.nodeId;
        if (rejectedNodeId !== null) {
          const rejectedNode = state.graph.nodes.find((node) => node.id === rejectedNodeId);
          const focusedNode = state.graph.nodes.find((node) => node.id === state.focusedNodeId);
          if (rejectedNode !== undefined && focusedNode !== undefined) {
            this.startRejectionAnimation(result.state, rejectedNode, focusedNode);
            return;
          }
        }
        this.startRejectionAnimation(result.state, undefined, undefined, direction);
      } else {
        this.renderState(result.state);
      }
    }
  }

  private startRejectionAnimation(
    state: LevelSelectState,
    rejectedNode?: LevelSelectNodeState,
    focusedNode?: LevelSelectNodeState,
    fallbackDirection?: LevelSelectDirection,
  ): void {
    this.isInputLocked = true;
    this.rejectionTween?.stop();
    this.rejectionTween = null;

    const token = this.runtimeToken;

    let dirX = 0;
    let dirY = 0;

    if (rejectedNode !== undefined && focusedNode !== undefined) {
      dirX = Math.sign(rejectedNode.position.x - focusedNode.position.x);
      dirY = Math.sign(rejectedNode.position.y - focusedNode.position.y);
    } else if (fallbackDirection !== undefined) {
      switch (fallbackDirection) {
        case "left":
          dirX = -1;
          break;
        case "right":
          dirX = 1;
          break;
        case "up":
          dirY = -1;
          break;
        case "down":
          dirY = 1;
          break;
      }
    }

    const offsetAmount = 12;
    const duration = 150;

    const anim = { x: 0, y: 0 };

    this.rejectionTween = this.tweens.add({
      targets: anim,
      x: dirX * offsetAmount,
      y: dirY * offsetAmount,
      duration: duration,
      yoyo: true,
      onUpdate: () => {
        if (token !== this.runtimeToken) {
          return;
        }
        this.levelSelectRenderer?.render(state, {
          cursorOffsetX: anim.x,
          cursorOffsetY: anim.y,
          cursorRed: true,
          chapters: this.chapters,
          save: this.controller?.getSave(),
          levels: this.levels,
        });
      },
      onComplete: () => {
        if (token !== this.runtimeToken) {
          return;
        }
        this.isInputLocked = false;
        this.rejectionTween = null;
        this.renderState(this.controller?.getState() ?? state);
      },
    });
  }

  private renderState(state: LevelSelectState): void {
    this.levelSelectRenderer?.render(state, {
      chapters: this.chapters,
      save: this.controller?.getSave(),
      levels: this.levels,
      passwordInputFlash: this.passwordInputFlash,
    });
  }

  private triggerPasswordInputFlash(): void {
    this.passwordInputFlashTimer?.remove();
    this.passwordInputFlash = true;
    if (this.controller !== null) {
      this.renderState(this.controller.getState());
    }
    this.passwordInputFlashTimer = this.time.delayedCall(150, () => {
      this.passwordInputFlash = false;
      this.passwordInputFlashTimer = null;
      if (this.controller !== null) {
        this.renderState(this.controller.getState());
      }
    });
  }

  private cancelPasswordInputFlash(): void {
    this.passwordInputFlashTimer?.remove();
    this.passwordInputFlashTimer = null;
    this.passwordInputFlash = false;
  }

  private triggerWhiteFlash(): void {
    const { width, height } = this.cameras.main;
    const flash = this.add.rectangle(width / 2, height / 2, width, height, 0xffffff);
    flash.setAlpha(1);
    flash.setScrollFactor(0);
    flash.setDepth(1000);

    this.tweens.add({
      targets: flash,
      alpha: 0,
      duration: END_UNDO_FLASH_DURATION_MS,
      onComplete: () => {
        flash.destroy();
      },
    });
  }

  private launchEndingLevel(): void {
    this.scene.start(LevelScene.KEY, {
      levelId: ENDING_LEVEL_ID,
      transitionIn: {
        color: "black",
        durationMs: LEVEL_SELECT_TO_LEVEL_FADE_IN_MS,
      },
    });
  }

  private startTransitionIn(): void {
    const transition = this.transitionIn;
    if (transition === null || transition.durationMs <= 0) {
      return;
    }

    const { width, height } = this.cameras.main;
    const overlay = this.add.rectangle(
      width / 2,
      height / 2,
      width,
      height,
      transition.color === "white" ? 0xffffff : 0x000000,
    );
    overlay.setAlpha(1);
    overlay.setScrollFactor(0);
    overlay.setDepth(1001);

    this.transitionInOverlay = overlay;
    this.isInputLocked = true;
    this.transitionInTween = this.tweens.add({
      targets: overlay,
      alpha: 0,
      duration: transition.durationMs,
      onComplete: () => {
        overlay.destroy();
        this.transitionInOverlay = null;
        this.transitionInTween = null;
        this.isInputLocked = false;
      },
    });
  }

  private async handlePasswordUnlock(result: LevelSelectTransitionResult): Promise<void> {
    if (this.controller === null) {
      return;
    }

    const token = this.runtimeToken;
    this.isInputLocked = true;
    this.renderState(result.state);

    try {
      await Promise.all([
        getRuntimeSession().updateSave(this.controller.getSave()),
        waitForDelay(PASSWORD_UNLOCK_SUCCESS_FLASH_MS),
      ]);

      if (token !== this.runtimeToken || this.controller === null) {
        return;
      }

      const settled = this.controller.settlePasswordUnlockSuccess();
      this.publishDebugState(settled.state);
      this.renderState(settled.state);
    } finally {
      if (token === this.runtimeToken) {
        this.isInputLocked = false;
      }
    }
  }

  private async handlePasswordFailure(result: LevelSelectTransitionResult): Promise<void> {
    if (this.controller === null) {
      return;
    }

    const token = this.runtimeToken;
    this.isInputLocked = true;
    this.cancelPasswordInputFlash();
    this.renderState(result.state);

    try {
      await waitForDelay(PASSWORD_FAILURE_FLASH_MS);

      if (token !== this.runtimeToken || this.controller === null) {
        return;
      }

      const settled = this.controller.settlePasswordFailure();
      this.publishDebugState(settled.state);
      this.renderState(settled.state);
    } finally {
      if (token === this.runtimeToken) {
        this.isInputLocked = false;
      }
    }
  }

  private bindCleanup(): void {
    this.events.once("shutdown", () => {
      this.cleanup();
    });
    this.events.once("destroy", () => {
      this.cleanup();
    });
  }

  private cleanup(): void {
    this.runtimeToken += 1;
    this.passwordInputFlashTimer?.remove();
    this.passwordInputFlashTimer = null;
    this.passwordInputFlash = false;
    this.destroyLevelSelectBgm();
    this.removeKeyboardListener();
    this.rejectionTween?.stop();
    this.rejectionTween = null;
    this.transitionInTween?.stop();
    this.transitionInTween = null;
    this.transitionInOverlay?.destroy();
    this.transitionInOverlay = null;
    this.isInputLocked = false;
    this.levelSelectRenderer?.destroy();
    this.levelSelectRenderer = null;
    this.controller = null;
  }

  private playLevelSelectBgm(): void {
    const existingSound = this.sound.get(LEVEL_SELECT_BGM_KEY);
    if (existingSound !== null) {
      existingSound.stop();
      existingSound.destroy();
    }

    this.levelSelectBgm = this.sound.add(LEVEL_SELECT_BGM_KEY, {
      loop: true,
      volume: DEFAULT_BGM_VOLUME,
    });
    this.levelSelectBgm.play();
  }

  private destroyLevelSelectBgm(): void {
    if (this.levelSelectBgm !== null) {
      this.levelSelectBgm.stop();
      this.levelSelectBgm.destroy();
      this.levelSelectBgm = null;
      return;
    }

    const existingSound = this.sound.get(LEVEL_SELECT_BGM_KEY);
    if (existingSound !== null) {
      existingSound.stop();
      existingSound.destroy();
    }
  }

  private removeKeyboardListener(): void {
    const keyboard = this.input.keyboard;
    if (keyboard !== undefined && keyboard !== null && this.keyboardListener !== null) {
      keyboard.off("keydown", this.keyboardListener);
    }
    this.keyboardListener = null;
  }

  private publishDebugState(_state: LevelSelectState): void {
    // Debug state display removed — see AGENTS.md TODO #9.
  }
}

function waitForDelay(durationMs: number): Promise<void> {
  return new Promise((resolve) => {
    globalThis.setTimeout(resolve, durationMs);
  });
}

function isLevelSelectDirection(value: string | null): value is LevelSelectDirection {
  return value === "left" || value === "right" || value === "up" || value === "down";
}

function isActionType(value: string | null): value is ActionType {
  return (
    value === "left" ||
    value === "right" ||
    value === "up" ||
    value === "down" ||
    value === "jump" ||
    value === "undo"
  );
}
